// Learning Programs — Vuex module
//
// Two concepts:
//   - Program: the definition. Title, description, ordered items[] with
//     relative offsetDays. Programs don't run anything by themselves —
//     they're recipes.
//   - Batch:   one instance of running a program. Specific startDate,
//     specific audience, its own progress, its own status. "Run again
//     for the next cohort" creates a new Batch from the same Program.
//
// Templates ship as Happily-authored Program definitions. New installs go
// through the installProgramTemplate Cloud Function, which creates/reuses
// real company-scoped library content first and returns a ready Program.
// The legacy cloneTemplate path remains for drafts created before the
// installer existed and for local fallback states.
//
// Persistence: programs and batches are written to Firestore collections
// `programs` and `program_batches`, both scoped by `company`. Templates
// are loaded from `program_template_catalog` with this module's fixture as
// a local fallback. The unified delivery
// service (happily-schedule-nest + Cloud Functions) reads `program_batches`
// to fan out items per-enrolee per-day; see the M2 plan for details.

import firebase from 'firebase/app';
import 'firebase/firestore';
import 'firebase/functions';

const PROGRAMS_COLLECTION = 'programs';
const BATCHES_COLLECTION = 'program_batches';
const TEMPLATE_CATALOG_COLLECTION = 'program_template_catalog';
const TEMPLATE_INSTALLS_COLLECTION = 'program_template_installs';

const SUPPORTED_TYPES = ['session', 'topic', 'poll', 'checklist', 'reel', 'custom_question', 'spark'] as const;
type ItemType = typeof SUPPORTED_TYPES[number];

let _seq = 0;
function newItemId(): string {
  _seq += 1;
  return 'it_' + Date.now().toString(36) + '_' + _seq.toString(36);
}

// shareMode controls per-batch cloning behaviour for topics, polls, and
// sessions. 'per_batch' (default) gives each batch its own clone with
// isolated comments / votes / signups. 'shared' uses the library item
// directly so rolling-enrolment programs (e.g., a new-joiners welcome
// topic) can have a single conversation that accumulates across batches.
// Has no effect on custom_question or spark items — those are per-user
// already.
type ShareMode = 'per_batch' | 'shared';

// completionReward fires after the participant has interacted with the
// item in the way appropriate to its type:
//   session         — attended the live session, or watched the video
//   topic           — read it
//   poll            — voted
//   checklist       — ticked all required items
//   reel            — watched the published reel
//   custom_question — answered
// The grant itself is written by the RN runtime when the delivery
// status flips to "completed" — this schema is the contract RN
// consumes. `reason` is the message surfaced to the recipient when
// they receive the grant (e.g., "For reading the team playbook"),
// seeded from the item title in the editor and freely editable by the
// author.
type Currency = 'coins' | 'gems' | 'xp';
type CompletionReward = { currency: Currency; amount: number; reason: string };

function intent(
  blueprintId: string,
  type: ItemType,
  offsetDays: number,
  participantTitle: string,
  completionReward: CompletionReward | null = null,
) {
  return {
    id: newItemId(),
    blueprintId,
    type,
    offsetDays,
    libraryId: null,
    participantTitle,
    shareMode: 'per_batch' as ShareMode,
    completionReward,
  };
}

const TEMPLATES_FIXTURE: any[] = [
  {
    id: 'tpl_first_30',
    title: 'Your first 30 days',
    description: 'For new hires. A scheduled welcome arc from day one to a 30-day check-in.',
    category: 'onboarding',
    duration: '30 days',
    audience: 'New hires',
    isHappilyAuthored: true,
    featured: true,
    content: {
      items: [
        intent('first_30_welcome_session', 'session',         0,  'Join your welcome session'),
        intent('first_30_culture_norms',   'topic',           1,  'Read how we work together'),
        intent('first_30_settling_in',     'custom_question', 3,  'Share what would help you settle in'),
        intent('first_30_first_week_checklist', 'checklist',  5,  'Complete your first-week essentials'),
        intent('first_30_team_playbook',   'topic',           7,  'Read the team playbook'),
        intent('first_30_mid_month',       'custom_question', 14, 'Reflect on your first two weeks'),
        intent('first_30_connection_spark', 'spark',          21, 'Try a small connection prompt'),
        // Day-30 milestone: completing the 30-day check-in drops 25 coins.
        intent('first_30_check_in_session', 'session',        30, 'Join your 30-day check-in',
               { currency: 'coins', amount: 25, reason: 'For completing your first 30 days' }),
      ],
    },
  },
  {
    id: 'tpl_leading_with_intention',
    title: 'Leading with intention',
    description: 'For first-time and seasoned managers. Twelve weeks of sessions, reflections, and pulse polls.',
    category: 'leadership',
    duration: '12 weeks',
    audience: 'Managers and leaders',
    isHappilyAuthored: true,
    content: {
      items: [
        intent('leading_foundations_session', 'session',         0,  'Join the leadership foundations session'),
        intent('leading_reflection',          'custom_question', 3,  'Name what your team can count on from you'),
        intent('leading_operating_rhythm',    'checklist',       7,  'Set your manager operating rhythm'),
        intent('leading_cohort_pulse',        'poll',            14, 'Share where your team needs more clarity'),
        intent('leading_feedback_session',    'session',         28, 'Practice feedback that lands'),
        intent('leading_feedback_reflection', 'custom_question', 35, 'Choose one useful feedback conversation'),
        intent('leading_prioritization',      'session',         56, 'Join the prioritization session'),
        intent('leading_micro_practices',     'spark',           70, 'Try a manager micro-practice'),
        intent('leading_wrap_up',             'session',         84, 'Join the cohort wrap-up'),
      ],
    },
  },
  {
    id: 'tpl_safe_and_sound',
    title: 'Safe & sound',
    description: 'Health and safety as a journey, not a checklist. Briefing, comprehension checks, and a final certification.',
    category: 'compliance',
    duration: '3 weeks',
    audience: 'All staff',
    isHappilyAuthored: true,
    content: {
      items: [
        intent('safety_briefing_session',     'session',         0,  'Join the safety briefing'),
        intent('safety_code_of_conduct',      'topic',           1,  'Read the code of conduct'),
        intent('safety_comprehension_check',  'custom_question', 3,  'Name one safe behavior you can practice'),
        intent('safety_emergency_procedures', 'topic',           7,  'Review emergency procedures'),
        intent('safety_readiness_checklist',  'checklist',       9,  'Complete your emergency readiness check'),
        intent('safety_identify_hazard',      'custom_question', 10, 'Identify one possible hazard'),
        intent('safety_confidence_poll',      'poll',            14, 'Share how prepared you feel'),
        // Certification carries a gems milestone — visible recognition for completion.
        intent('safety_certification_session', 'session',        20, 'Join the safety certification review',
               { currency: 'gems', amount: 5, reason: 'For passing the safety certification' }),
      ],
    },
  },
  {
    id: 'tpl_take_care',
    title: 'Take care',
    description: 'Four weeks of small moments — a baseline pulse, a reflection, and a follow-up.',
    category: 'wellbeing',
    duration: '4 weeks',
    audience: 'All staff',
    isHappilyAuthored: true,
    content: {
      items: [
        intent('take_care_baseline_energy', 'custom_question', 0,  'A baseline energy/mood pulse (1–10 scale)'),
        intent('take_care_benefits_read',   'topic',           2,  'Read the well-being support guide'),
        intent('take_care_reset_plan',      'checklist',       5,  'Make one small reset plan'),
        intent('take_care_team_mood',       'poll',            10, 'Share what would help the team this week'),
        intent('take_care_small_resets',    'spark',           14, 'Try a small reset prompt'),
        intent('take_care_helped_reflect',  'custom_question', 17, 'Reflect on what helped this week'),
        intent('take_care_followup_energy', 'custom_question', 28, 'A follow-up energy pulse (1–10 scale)'),
      ],
    },
  },
];

function deepClone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value));
}

function newProgramId(): string {
  return 'pg_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

function newBatchId(): string {
  return 'b_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

function templateWithMeta(tpl: any) {
  return {
    ...tpl,
    items: (tpl.content && tpl.content.items && tpl.content.items.length) || 0,
  };
}

const TEMPLATES_WITH_META = TEMPLATES_FIXTURE.map((tpl) => templateWithMeta(tpl));

function installDocId(company: string, templateId: string): string {
  return `${String(company || '').replace(/[\/.#$\[\]]/g, '_')}__${templateId}`;
}

export function templateItemKey(item: any, index: number = 0): string {
  if (!item) return `item_${index}`;
  return item.blueprintId || item.templateItemId || item.id || `${item.type || 'item'}_${index}`;
}

export function buildTemplateInstallPreview(template: any, install: any = null) {
  const itemMap = (install && install.itemMap) || {};
  const items = (template && template.content && template.content.items) || [];
  const rows = items.map((item: any, index: number) => {
    const key = templateItemKey(item, index);
    const existing = itemMap[key] || null;
    return {
      key,
      type: item.type,
      title: item.participantTitle || '',
      offsetDays: Number(item.offsetDays) || 0,
      libraryId: existing && existing.libraryId ? existing.libraryId : '',
      action: existing && existing.libraryId ? 'reuse' : 'create',
    };
  });
  return {
    templateId: template && template.id,
    installed: !!(install && install.installedAt),
    rows,
    createCount: rows.filter((row: any) => row.action === 'create').length,
    reuseCount: rows.filter((row: any) => row.action === 'reuse').length,
  };
}

export default {
  namespaced: true,
  state: {
    templates: TEMPLATES_WITH_META,
    programs: [] as any[],          // Program definitions
    batches: [] as any[],           // Batch instances
    loading: false,
    loadingPrograms: false,
    loadingBatches: false,
    programsError: '',
    batchesError: '',
    inputSearch: '',
  },
  mutations: {
    setTemplates(state: any, templates: any[]) {
      state.templates = templates && templates.length ? templates : TEMPLATES_WITH_META;
    },
    setLoading(state: any, value: boolean) {
      state.loading = value;
    },
    setProgramsLoading(state: any, value: boolean) {
      state.loadingPrograms = value;
    },
    setBatchesLoading(state: any, value: boolean) {
      state.loadingBatches = value;
    },
    setProgramsError(state: any, value: string) {
      state.programsError = value || '';
    },
    setBatchesError(state: any, value: string) {
      state.batchesError = value || '';
    },
    setPrograms(state: any, programs: any[]) {
      state.programs = programs || [];
    },
    upsertProgram(state: any, program: any) {
      const idx = state.programs.findIndex((p: any) => p.id === program.id);
      if (idx >= 0) state.programs.splice(idx, 1, program);
      else state.programs.push(program);
    },
    removeProgram(state: any, id: string) {
      state.programs = state.programs.filter((p: any) => p.id !== id);
    },
    setBatches(state: any, batches: any[]) {
      state.batches = batches || [];
    },
    upsertBatch(state: any, batch: any) {
      const idx = state.batches.findIndex((b: any) => b.id === batch.id);
      if (idx >= 0) state.batches.splice(idx, 1, batch);
      else state.batches.push(batch);
    },
    removeBatch(state: any, id: string) {
      state.batches = state.batches.filter((b: any) => b.id !== id);
    },
    setSearch(state: any, value: string) {
      state.inputSearch = value;
    },
  },
  actions: {
    async fetchTemplates({ commit, state }: any) {
      try {
        const snap = await firebase
          .firestore()
          .collection(TEMPLATE_CATALOG_COLLECTION)
          .where('published', '==', true)
          .get();
        const templates = snap.docs
          .map((doc: any) => templateWithMeta({ id: doc.id, ...doc.data() }))
          .filter((tpl: any) => tpl.content && Array.isArray(tpl.content.items))
          .sort((a: any, b: any) => {
            if (a.featured && !b.featured) return -1;
            if (!a.featured && b.featured) return 1;
            return (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0);
          });
        if (templates.length) {
          commit('setTemplates', templates);
          return templates;
        }
      } catch (err) {
        // The fixture keeps the builder usable before the master catalog is
        // seeded or opened in Firestore rules.
      }
      return state.templates;
    },
    async fetchTemplateInstall({ rootState }: any, templateId: string) {
      const company = rootState && rootState.authen && rootState.authen.user
        ? rootState.authen.user.company
        : null;
      if (!company || !templateId) return null;
      const snap = await firebase
        .firestore()
        .collection(TEMPLATE_INSTALLS_COLLECTION)
        .doc(installDocId(company, templateId))
        .get();
      return snap.exists ? { id: snap.id, ...snap.data() } : null;
    },
    async previewTemplateInstall({ state, dispatch }: any, templateId: string) {
      const template = state.templates.find((t: any) => t.id === templateId);
      if (!template) return null;
      const install = await dispatch('fetchTemplateInstall', templateId);
      return buildTemplateInstallPreview(template, install);
    },
    async installTemplate({ commit, dispatch, rootState }: any, payload: any) {
      const company = rootState && rootState.authen && rootState.authen.user
        ? rootState.authen.user.company
        : null;
      if (!company) {
        throw new Error('installTemplate: missing company on user');
      }
      const templateId = payload && payload.templateId;
      if (!templateId) {
        throw new Error('installTemplate: missing templateId');
      }
      const installProgramTemplate = (firebase.app() as any)
        .functions('us-central1')
        .httpsCallable('installProgramTemplate');
      const result = await installProgramTemplate({
        company,
        templateId,
        title: payload.title || '',
        description: payload.description || '',
        programId: payload.programId || '',
      });
      const data = (result && result.data) || {};
      if (data.program) {
        commit('upsertProgram', data.program);
      }
      // Refresh the install map so the preview immediately flips from
      // "create" to "reuse" after a successful install.
      await dispatch('fetchTemplateInstall', templateId).catch(() => null);
      return data;
    },
    async fetchPrograms({ commit, rootState }: any) {
      // Read all programs scoped to the current company.
      const company = rootState && rootState.authen && rootState.authen.user
        ? rootState.authen.user.company
        : null;
      if (!company) {
        commit('setPrograms', []);
        commit('setProgramsError', '');
        return;
      }
      commit('setLoading', true);
      commit('setProgramsLoading', true);
      commit('setProgramsError', '');
      try {
        // Sort client-side by updatedAt: the company filter alone uses
        // Firestore's automatic single-field index, so this avoids a
        // composite index for a small per-company collection.
        const snap = await firebase
          .firestore()
          .collection(PROGRAMS_COLLECTION)
          .where('company', '==', company)
          .get();
        const programs = snap.docs
          .map((d: any) => ({ id: d.id, ...d.data() }))
          .sort((a: any, b: any) =>
            String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')));
        commit('setPrograms', programs);
      } catch (err) {
        // Keep any previously loaded rows in place. The UI owns a distinct
        // error state so a connection problem is never presented as an
        // empty company account.
        // tslint:disable-next-line:no-console
        console.error('[programs] fetchPrograms failed:', err);
        commit('setProgramsError', 'Programs could not be loaded. Check your connection and try again.');
      } finally {
        commit('setLoading', false);
        commit('setProgramsLoading', false);
      }
    },
    async fetchBatches({ commit, rootState }: any) {
      const company = rootState && rootState.authen && rootState.authen.user
        ? rootState.authen.user.company
        : null;
      if (!company) {
        commit('setBatches', []);
        commit('setBatchesError', '');
        return;
      }
      commit('setBatchesLoading', true);
      commit('setBatchesError', '');
      try {
        // Sorted client-side by createdAt — same reasoning as
        // fetchPrograms: no composite index needed.
        const snap = await firebase
          .firestore()
          .collection(BATCHES_COLLECTION)
          .where('company', '==', company)
          .get();
        const batches = snap.docs
          .map((d: any) => ({ id: d.id, ...d.data() }))
          .sort((a: any, b: any) =>
            String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
        commit('setBatches', batches);
      } catch (err) {
        // tslint:disable-next-line:no-console
        console.error('[programs] fetchBatches failed:', err);
        commit('setBatchesError', 'Runs could not be loaded. Check your connection and try again.');
      } finally {
        commit('setBatchesLoading', false);
      }
    },
    cloneTemplate({ state }: any, templateId: string) {
      // Deep-clone a template's content into a fresh draft Program.
      // Returns the new program object (not yet persisted; saveProgram
      // writes it to Firestore).
      const tpl = state.templates.find((t: any) => t.id === templateId);
      if (!tpl) return null;
      const content = deepClone(tpl.content || { items: [] });
      const items = (content.items || []).map((it: any) => ({ ...it, id: newItemId() }));
      return {
        id: newProgramId(),
        title: tpl.title || '',
        description: tpl.description || '',
        templateRef: tpl.id,
        status: 'draft',
        items,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
    },
    async saveProgram({ commit, rootState }: any, program: any) {
      // Persist a Program definition to Firestore. If every item has a
      // libraryId the program is "ready" to be run as a Batch; otherwise
      // "draft."
      const company = rootState && rootState.authen && rootState.authen.user
        ? rootState.authen.user.company
        : null;
      if (!company) {
        throw new Error('saveProgram: missing company on user');
      }
      const items = (program.items || []).map((it: any) => ({
        // Default shareMode for any pre-existing item that predates this
        // field, so reads of older programs don't surface `undefined` to
        // the runtime.
        shareMode: 'per_batch' as ShareMode,
        ...it,
      }));
      const ready = items.length > 0 && items.every((it: any) => !!it.libraryId);
      const next = {
        ...program,
        id: program.id || newProgramId(),
        company,
        status: ready ? 'ready' : 'draft',
        items,
        updatedAt: new Date().toISOString(),
        createdAt: program.createdAt || new Date().toISOString(),
      };
      // Strip any legacy enrollment field — Batches own audience now.
      delete next.enrollment;
      // Firestore doesn't allow undefined values; strip them defensively.
      Object.keys(next).forEach((k) => {
        if (next[k] === undefined) delete next[k];
      });
      try {
        const { id, ...payload } = next;
        await firebase
          .firestore()
          .collection(PROGRAMS_COLLECTION)
          .doc(id)
          .set(payload, { merge: true });
        commit('upsertProgram', next);
      } catch (err) {
        // tslint:disable-next-line:no-console
        console.error('[programs] saveProgram failed:', err);
        throw err;
      }
      return next;
    },
    async createBatch({ commit, state, rootState }: any, payload: any) {
      // payload: { programId, name, startDate (ISO), audienceSnapshot }
      // Writes a program_batches/{id} doc to Firestore. The CF
      // onProgramBatchCreate trigger picks it up and dispatches to
      // happily-schedule-nest, which expands audience, clones content
      // per batch, and writes users_deliveries rows.
      const company = rootState && rootState.authen && rootState.authen.user
        ? rootState.authen.user.company
        : null;
      if (!company) {
        throw new Error('createBatch: missing company on user');
      }
      const program = state.programs.find((p: any) => p.id === payload.programId);
      if (!program) {
        throw new Error('createBatch: program not found ' + payload.programId);
      }
      const startDateMs = payload.startDate ? new Date(payload.startDate).getTime() : Date.now();
      const isFuture = startDateMs > Date.now() + 60 * 1000;
      const batch: any = {
        id: newBatchId(),
        programId: program.id,
        programTitle: program.title,
        company,
        name: payload.name || `${program.title} — Batch`,
        startDate: payload.startDate || new Date().toISOString(),
        audienceSnapshot: payload.audienceSnapshot || { selections: [] },
        enrolees: [] as string[],         // schedule-nest populates this on process
        itemClones: [] as any[],          // schedule-nest populates this on process
        status: isFuture ? 'scheduled' : 'active',
        createdAt: new Date().toISOString(),
        publishedAt: new Date().toISOString(),
      };
      try {
        const { id, ...rest } = batch;
        await firebase
          .firestore()
          .collection(BATCHES_COLLECTION)
          .doc(id)
          .set(rest);
        commit('upsertBatch', batch);
      } catch (err) {
        // tslint:disable-next-line:no-console
        console.error('[programs] createBatch failed:', err);
        throw err;
      }
      return batch;
    },
    async archiveProgram({ commit, state }: any, programId: string) {
      // Soft-hide: the program stays in Firestore (status='archived')
      // and is filtered out of the list. Reversible by clearing status.
      const program = state.programs.find((p: any) => p.id === programId);
      if (!program) return null;
      const next = { ...program, status: 'archived', updatedAt: new Date().toISOString() };
      try {
        await firebase
          .firestore()
          .collection(PROGRAMS_COLLECTION)
          .doc(programId)
          .update({ status: 'archived', updatedAt: next.updatedAt });
        commit('upsertProgram', next);
      } catch (err) {
        // tslint:disable-next-line:no-console
        console.error('[programs] archiveProgram failed:', err);
        throw err;
      }
      return next;
    },
    async restoreProgram({ commit, state }: any, programId: string) {
      const program = state.programs.find((p: any) => p.id === programId);
      if (!program) return null;
      const items = Array.isArray(program.items) ? program.items : [];
      const status = items.length > 0 && items.every((item: any) => !!item.libraryId)
        ? 'ready'
        : 'draft';
      const updatedAt = new Date().toISOString();
      const next = { ...program, status, updatedAt };
      try {
        await firebase
          .firestore()
          .collection(PROGRAMS_COLLECTION)
          .doc(programId)
          .update({ status, updatedAt });
        commit('upsertProgram', next);
      } catch (err) {
        // tslint:disable-next-line:no-console
        console.error('[programs] restoreProgram failed:', err);
        throw err;
      }
      return next;
    },
    async deleteProgram({ commit }: any, programId: string) {
      // Hard delete. Existing batches are independent docs and are left
      // intact — they carry their own cloned content.
      try {
        await firebase
          .firestore()
          .collection(PROGRAMS_COLLECTION)
          .doc(programId)
          .delete();
        commit('removeProgram', programId);
      } catch (err) {
        // tslint:disable-next-line:no-console
        console.error('[programs] deleteProgram failed:', err);
        throw err;
      }
      return programId;
    },
    async archiveBatch({ commit, state }: any, batchId: string) {
      const batch = state.batches.find((b: any) => b.id === batchId);
      if (!batch) return null;
      const next = { ...batch, status: 'archived', updatedAt: new Date().toISOString() };
      try {
        await firebase
          .firestore()
          .collection(BATCHES_COLLECTION)
          .doc(batchId)
          .update({ status: 'archived', updatedAt: next.updatedAt });
        commit('upsertBatch', next);
      } catch (err) {
        // tslint:disable-next-line:no-console
        console.error('[programs] archiveBatch failed:', err);
        throw err;
      }
      return next;
    },
    async deleteBatch({ commit }: any, batchId: string) {
      // Hard delete — used to undo a batch started by mistake. Deleting the
      // program_batches/{id} doc fires the onProgramBatchDelete Cloud
      // Function, which cascade-deletes the per-user users_deliveries /
      // users_questions rows this batch fanned out, so the content
      // disappears from every enrolee's app. Not reversible.
      try {
        await firebase
          .firestore()
          .collection(BATCHES_COLLECTION)
          .doc(batchId)
          .delete();
        commit('removeBatch', batchId);
      } catch (err) {
        // tslint:disable-next-line:no-console
        console.error('[programs] deleteBatch failed:', err);
        throw err;
      }
      return batchId;
    },
    async retryBatch({ dispatch, state }: any, batchId: string) {
      // A batch ends up in `process_failed` when onProgramBatchCreate's
      // processBatch run returns ok:false or throws (see programsRuntime.js).
      // The trigger only fires onCreate, so to "retry" we delete the failed
      // batch (cascade-cleans any partial deliveries / clones via
      // onProgramBatchDelete) and create a fresh batch with the same
      // params — same program, same start date, same audience.
      const batch = state.batches.find((b: any) => b.id === batchId);
      if (!batch) {
        throw new Error('retryBatch: batch not found ' + batchId);
      }
      const payload = {
        programId: batch.programId,
        name: batch.name,
        startDate: batch.startDate,
        audienceSnapshot: batch.audienceSnapshot || { selections: [] },
      };
      try {
        await dispatch('deleteBatch', batchId);
        return await dispatch('createBatch', payload);
      } catch (err) {
        // tslint:disable-next-line:no-console
        console.error('[programs] retryBatch failed:', err);
        throw err;
      }
    },
  },
  getters: {
    templates: (state: any) => state.templates,
    featuredTemplate: (state: any) =>
      state.templates.find((t: any) => t.featured) || null,
    programs: (state: any) => state.programs,
    drafts: (state: any) => state.programs.filter((p: any) => p.status === 'draft'),
    readyPrograms: (state: any) => state.programs.filter((p: any) => p.status === 'ready'),
    batches: (state: any) => state.batches,
    activeBatches: (state: any) => state.batches.filter((b: any) => b.status === 'active'),
    scheduledBatches: (state: any) => state.batches.filter((b: any) => b.status === 'scheduled'),
    completedBatches: (state: any) => state.batches.filter((b: any) => b.status === 'completed'),
    batchesForProgram: (state: any) => (programId: string) =>
      state.batches.filter((b: any) => b.programId === programId),
    batchCountForProgram: (state: any) => (programId: string) =>
      state.batches.filter((b: any) => b.programId === programId).length,
    counts: (state: any) => ({
      templates: state.templates.length,
      programs: state.programs.length,
      batches: state.batches.length,
      activeBatches: state.batches.filter((b: any) => b.status === 'active').length,
      scheduledBatches: state.batches.filter((b: any) => b.status === 'scheduled').length,
      completedBatches: state.batches.filter((b: any) => b.status === 'completed').length,
      failedBatches: state.batches.filter((b: any) => b.status === 'process_failed').length,
    }),
    loading: (state: any) => state.loading,
    loadingPrograms: (state: any) => state.loadingPrograms,
    loadingBatches: (state: any) => state.loadingBatches,
    programsError: (state: any) => state.programsError,
    batchesError: (state: any) => state.batchesError,
  },
};
